import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import './MembershipModelGuide.css';

/**
 * In-app guide for the membership model discussed in
 * docs/anchor-and-extending-memberships.md — collapsible sections.
 * Client-only; no API.
 */

function GuideSection({ id, title, summary, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <section className={`mmg-section${open ? ' is-open' : ''}`} id={id}>
      <button
        type="button"
        className="mmg-section-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="mmg-section-chevron" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
        <span className="mmg-section-titles">
          <span className="mmg-section-title">{title}</span>
          {summary ? <span className="mmg-section-summary">{summary}</span> : null}
        </span>
      </button>
      <div id={panelId} className="mmg-section-body" hidden={!open}>
        {children}
      </div>
    </section>
  );
}

export default function MembershipModelGuide() {
  return (
    <div className="mmg-page">
      <header className="mmg-header">
        <p className="mmg-eyebrow">Membership model · reference</p>
        <h1>Anchor, extending, awards &amp; badges</h1>
        <p>
          Everything we settled for UIS memberships: what you sign in as, what you merely belong
          to, what lasts forever as an achievement, and what is only a temporary tag. Matches{' '}
          <code>docs/anchor-and-extending-memberships.md</code>.
        </p>
        <nav className="mmg-links" aria-label="Related pages">
          <Link to="/membership-model-demo">Membership sandbox</Link>
          <Link to="/groups-model-demo">Groups web map demo</Link>
          <Link to="/groups">Groups (live)</Link>
        </nav>
      </header>

      <div className="mmg-toc">
        <a href="#overview">Overview</a>
        <a href="#anchor-extending">Anchor vs extending</a>
        <a href="#mixing-containers">Mixing &amp; containers</a>
        <a href="#groups-vs-apps">Groups vs apps</a>
        <a href="#awards-badges">Awards vs badges</a>
        <a href="#changing-kind">Updating membership_kind</a>
        <a href="#periods">Periods &amp; access</a>
        <a href="#why-ugp-periods">Why UGP &amp; periods?</a>
        <a href="#permissions-platform">Permissions &amp; multi-site</a>
        <a href="#display-labels">Display labels</a>
        <a href="#phases">Implementation phases</a>
        <a href="#future-tasks">Future tasks &amp; concerns</a>
      </div>

      <GuideSection
        id="overview"
        title="Overview — mental model"
        summary="UGPs stay the enrollment spine; groups declare what membership means"
        defaultOpen
      >
        <p>
          Every membership is still a normal <code>user_groups_periods</code> (UGP) row. Nothing
          about enrollment, rosters, periods, or hierarchy changes. What changes is that the{' '}
          <strong>group</strong> declares what membership means via{' '}
          <code>groups.membership_kind</code> (<code>anchor</code> | <code>extending</code>), and
          the app treats the two kinds differently.
        </p>
        <pre className="mmg-tree" aria-label="Example membership tree">{`users
 └── UGP @ Students · 2026–2027            ← ANCHOR (sign-in identity)
       ├── UGP @ Basketball roster · Fall  ← extending (affiliation + apps)
       ├── UGP @ Chess Club · 2026–2027    ← extending (thin)
       └── Award: Dean's List · Fall 2026  ← award (relationship); NO UGP
            (+ Fall 2025 award still shows — awards accumulate)`}</pre>
        <table className="mmg-table">
          <thead>
            <tr>
              <th>Term</th>
              <th>Meaning</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Anchor</strong>
              </td>
              <td>
                Identity you act through — switcher, <code>current_ugp_id</code>, base permissions,
                profile context (Students, Teachers, Admin, Coaches).
              </td>
            </tr>
            <tr>
              <td>
                <strong>Extending</strong>
              </td>
              <td>
                Real UGP for clubs / teams / containers. Owns its data; never a sign-in context.
                Current profile display uses <strong>window overlap</strong> with the viewing year.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Award</strong>
              </td>
              <td>
                Dated achievement (Dean's List · Fall 2026). Relationship row — usually not a group.
                <strong> Persists</strong> across years.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Badge / tag</strong>
              </td>
              <td>
                Current or UI-only chrome (see below). Temporary or contextual — not a lifetime
                achievement history.
              </td>
            </tr>
          </tbody>
        </table>
        <p className="mmg-note">
          There is no separate “role” table. A role is simply an anchor membership.
        </p>
      </GuideSection>

      <GuideSection
        id="anchor-extending"
        title="Anchor vs extending"
        summary="Default create UI to extending; choose anchor only for primary identity"
      >
        <table className="mmg-table">
          <thead>
            <tr>
              <th>Behavior</th>
              <th>Anchor UGP</th>
              <th>Extending UGP</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Role switcher</td>
              <td>Yes</td>
              <td>
                <strong>Never</strong>
              </td>
            </tr>
            <tr>
              <td>
                Eligible as <code>current_ugp_id</code>
              </td>
              <td>Yes</td>
              <td>
                <strong>Never</strong>
              </td>
            </tr>
            <tr>
              <td>Sign-in / acting context</td>
              <td>Yes</td>
              <td>No — you never “sign in as basketball”</td>
            </tr>
            <tr>
              <td>Permissions</td>
              <td>Base grants</td>
              <td>Optional grants, unioned onto the active anchor (same org; Phase 4)</td>
            </tr>
            <tr>
              <td>Profile</td>
              <td>Identity + role data</td>
              <td>Sections under the anchor profile; click → membership detail</td>
            </tr>
            <tr>
              <td>Current-year display</td>
              <td>Always the viewing identity</td>
              <td>Only if membership window <strong>overlaps</strong> viewing context</td>
            </tr>
          </tbody>
        </table>
        <h3>When creating a group</h3>
        <ul>
          <li>
            <strong>UI default is always Extending</strong> — clubs, rosters, Sports, Basketball,
            committees.
          </li>
          <li>
            Choose <strong>Anchor</strong> only if this is the member’s{' '}
            <em>primary identity/role</em> at the org — the thing they sign in as (Students,
            Teachers, Admin, Coaches). Not “captain of chess” or “player on a roster.”
          </li>
          <li>
            The create sheet shows the kind toggle when creating a <em>new</em> group (org-level or
            nested team) — not when only adding another period on an existing group.
          </li>
        </ul>
        <h3>Same org, different rules</h3>
        <p>
          You can join an extending group without sharing the parent’s anchor team (Exchange Student
          + Chess Club nested under Students is fine). Scope for profile aggregation / permission
          union is <strong>same organization</strong>, not “same parent group.”
        </p>
      </GuideSection>

      <GuideSection
        id="mixing-containers"
        title="Mixing kinds &amp; container groups"
        summary="Sports → Basketball → Roster (extending) with Coaches (anchor) nested under is legal"
      >
        <p>
          Kind is <strong>per-group and never inherited</strong>. Tree position answers “where does
          this live?” Kind answers “what does membership mean?”
        </p>
        <pre className="mmg-tree">{`School A
├─ Students        anchor
│  └─ Chess Club   extending
├─ Sports          extending   ← container
│  └─ Basketball   extending   ← container
│     ├─ Team A    extending   ← players enroll here
│     └─ Coaches   anchor      ← identity under extending parents
└─ Teachers        anchor`}</pre>
        <h3>Container groups (Sports, Basketball) — and the organization itself</h3>
        <p>
          Mostly organize the tree; often have <strong>no direct members</strong>. Still use{' '}
          <strong>extending</strong> — not <code>NULL</code>, not a third enum value. If someone
          enrolls on Sports (coordinator), extending is still right. The organization root is the
          same shape: not a sign-in identity, so do <strong>not</strong> mark it{' '}
          <code>anchor</code>.
        </p>
        <p>
          <strong>Today:</strong> kind is about <em>direct</em> UGPs. Empty containers (org, Sports)
          do not push relationships or grants down the tree — a Varsity roster member does not
          automatically pick up whatever hangs off Sports. So for org/Sports,{' '}
          <code>membership_kind</code> is mostly a label that says “never a switcher context,” plus
          readiness for the inheritance work below.
        </p>
        <p>
          <strong>Why keep extending (not NULL / not a new name):</strong> switcher and eligibility
          stay simple (<code>kind = 'anchor'</code> only). A third value (
          <code>structural</code> / <code>container</code>) can wait until we need to distinguish
          “may later inherit to descendants” from “pure tree node forever.” Prefer that over{' '}
          <code>NULL</code> in a NOT NULL check.
        </p>
        <p>
          Lifetime recognitions (Dean's List) use <strong>awards</strong>, not container membership
          alone.
        </p>
      </GuideSection>

      <GuideSection
        id="groups-vs-apps"
        title="Groups vs apps (what belongs in the tree)"
        summary="Course enrollment is membership — but not every membership is a UIS group"
      >
        <p>
          Being on Basketball · Fall and being enrolled in Algebra · P3 · Fall are both membership:
          person ↔ container ↔ period. The cut is not “membership vs not” — it is{' '}
          <strong>which membership system</strong> and whether the container belongs in the org
          tree.
        </p>
        <p>
          Put something in <strong>groups</strong> when it is a real org unit people affiliate with
          and the count stays manageable on the map: Sports → Basketball → roster / coaches, Chess
          Club, committees. Nesting under a specific groups_period stays correct because affiliation
          hangs off a time-scoped team context.
        </p>
        <p>
          Prefer an <strong>app with its own tables</strong> when the thing is a fat catalog or
          schedule and would flood the tree — courses, sections / classrooms (codes, rooms,
          capacity, grades, assignments). A school may have a handful of sports teams and hundreds
          of sections per term; the latter is odd as nested <code>team_nested</code> groups even
          though enrollment is still membership.
        </p>
        <table className="mmg-table">
          <thead>
            <tr>
              <th></th>
              <th>Team / club (groups)</th>
              <th>Course section (app)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>How many per person</td>
              <td>Usually few</td>
              <td>Often many per term</td>
            </tr>
            <tr>
              <td>Org chart role</td>
              <td>Real nested team under Sports / Students</td>
              <td>Scheduling / academic unit — not a folder under Students</td>
            </tr>
            <tr>
              <td>Extra data</td>
              <td>Thin (name, roster, apps/grants on the gp)</td>
              <td>Fat (catalog, room, grades, assignments)</td>
            </tr>
            <tr>
              <td>Acting context</td>
              <td>Sometimes affiliation; rarely a switcher</td>
              <td>Almost never “sign in as Algebra P3”</td>
            </tr>
            <tr>
              <td>Where it lives</td>
              <td>
                <code>groups</code> + <code>groups_periods</code> + UGP
              </td>
              <td>
                App tables (e.g. courses / sections / enrollments), linked to the student’s
                school-year UGP or gp for “who is a student this year”
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <strong>Sports apps</strong> still make sense <em>alongside</em> Basketball-as-a-group:
          keep the roster / coaches in UIS; put games, stats, lineups, jersey numbers in the sports
          app pointing at that gp. Domains enhance the team — they do not replace the affiliation
          container.
        </p>
        <p className="mmg-note">
          Rule of thumb: org-group membership when the group is an org unit people affiliate with;
          app enrollment tables when the product is a large catalog/schedule. Hierarchy stays
          gp→gp for teams; do not invent year-first folders just to hold every course.
        </p>
      </GuideSection>

      <GuideSection
        id="awards-badges"
        title="Awards vs badges / tags"
        summary="Awards accumulate with a term; badges are current or UI chrome"
        defaultOpen
      >
        <p>
          Prefer <strong>Awards</strong> for profile history. “Badge” as a product noun sounded like
          a <em>current</em> label that disappears — wrong for Dean’s List. We still use badges/tags
          for temporary or UI status.
        </p>

        <h3>Awards</h3>
        <ul>
          <li>
            Dated achievements: “Dean's List · Fall 2026”, “MVP · 2025”.
          </li>
          <li>
            Stored as <code>relationships</code> rows (e.g. <code>source_slug = designations</code>
            ), usually <strong>not</strong> a group — unless you also need a live <em>roster</em>{' '}
            for who is on the list <em>this</em> term.
          </li>
          <li>
            <strong>Must keep previous awards.</strong> Off Dean’s List this year → Fall 2025 still
            shows under Awards. Do not clear history when the current list changes.
          </li>
          <li>
            Profile should load awards across same-org years (not only the currently viewed Student
            UGP), so history survives rollover.
          </li>
        </ul>

        <h3>When a badge / tag still makes sense</h3>
        <p>
          Use a badge/tag when the signal is <strong>current, contextual, or UI-only</strong> — not
          a lifetime achievement you want in an Awards timeline.
        </p>
        <table className="mmg-table">
          <thead>
            <tr>
              <th>Example</th>
              <th>Why it’s a badge/tag, not an award</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>“Athlete”</strong> while enrolled under Sports / a roster UGP
              </td>
              <td>
                Current affiliation chip — disappears from <em>current</em> chips when the roster
                window ends. History of seasons lives on extending memberships / Past memberships,
                not an award unless you also grant “Letter winner · 2026”.
              </td>
            </tr>
            <tr>
              <td>
                Nav <strong>“Past period — override”</strong>
              </td>
              <td>Operational warning for the active session, not a profile achievement.</td>
            </tr>
            <tr>
              <td>
                Groups tree <strong>anchor</strong> / <strong>period</strong> / type pills
              </td>
              <td>Admin UI chrome describing the group/gp node.</td>
            </tr>
            <tr>
              <td>
                Work-sample / TOR flags like <strong>“Needs review”</strong>
              </td>
              <td>Workflow state on an item — clears when resolved.</td>
            </tr>
            <tr>
              <td>
                Soft tags: <strong>“Guest”</strong>, <strong>“Exchange”</strong> on a roster row
              </td>
              <td>
                Contextual filters/labels during the affiliation — not “won Guest 2024” history.
              </td>
            </tr>
            <tr>
              <td>
                Permission / feature chips on a config screen
              </td>
              <td>UI only — never profile history.</td>
            </tr>
          </tbody>
        </table>

        <h3>Quick contrast</h3>
        <table className="mmg-table">
          <thead>
            <tr>
              <th></th>
              <th>Extending membership</th>
              <th>Award</th>
              <th>Badge / tag</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Meaning</td>
              <td>Affiliated in a window</td>
              <td>Earned at a term</td>
              <td>Current or UI status</td>
            </tr>
            <tr>
              <td>Display</td>
              <td>Overlap-filtered (+ optional Past)</td>
              <td>
                <strong>Accumulates</strong>
              </td>
              <td>Shows while true / in that UI</td>
            </tr>
            <tr>
              <td>Example leave mid-year</td>
              <td>End roster UGP</td>
              <td>Dean's List Fall still listed</td>
              <td>“Athlete” chip goes away</td>
            </tr>
          </tbody>
        </table>
        <p className="mmg-note">
          Live Dean’s List <em>roster</em> group (extending, this semester) is optional for admins.
          Lifetime recognition still writes an <strong>award</strong> row so last year stays on the
          profile.
        </p>
      </GuideSection>

      <GuideSection
        id="changing-kind"
        title="Updating membership_kind later"
        summary="Dangerous — treat as a migration, not a casual edit"
      >
        <p>
          Kind lives on <code>groups</code> and applies <strong>retroactively to every member</strong>{' '}
          of that group. Not per-UGP, not versioned.
        </p>
        <table className="mmg-table">
          <thead>
            <tr>
              <th>Direction</th>
              <th>Risk</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>anchor → extending</strong>
              </td>
              <td>
                UGPs leave the switcher; <code>current_ugp_id</code> on those UGPs becomes invalid.
                Users may need reassignment to another anchor.
              </td>
            </tr>
            <tr>
              <td>
                <strong>extending → anchor</strong>
              </td>
              <td>
                UGPs enter the switcher. Someone who already has Student may suddenly have{' '}
                <strong>two anchors</strong> in one org.
              </td>
            </tr>
          </tbody>
        </table>
        <h3>Do</h3>
        <ul>
          <li>Set kind carefully at create time (UI defaults to extending).</li>
          <li>
            If you must change: audit active UGPs, <code>current_ugp_id</code>, and other anchors in
            the same org.
          </li>
          <li>
            Prefer <strong>new group + move members</strong> over an in-place flip when the mistake
            is structural.
          </li>
        </ul>
        <h3>Don’t</h3>
        <ul>
          <li>Flip kind to fix one confused user — everyone on the group inherits it.</li>
          <li>
            Use kind changes instead of new UGP rows for time boundaries (leave / rejoin → end old
            UGP, create new stint).
          </li>
        </ul>
        <p className="mmg-note">
          Phase 1: create-time toggle only — no edit-kind UI yet. Backfilled groups all started as{' '}
          <code>anchor</code>; curate Sports / Basketball / clubs to <code>extending</code> via
          controlled SQL when ready.
        </p>
      </GuideSection>

      <GuideSection
        id="periods"
        title="Periods, nesting &amp; access"
        summary="Branch from group-periods; Permanent OK for lasting teams; past override warns"
      >
        <ul>
          <li>
            Nesting is <strong>gp → gp</strong> (<code>team_nested</code>). Branch under a period
            card (or ghost nested team), not a bare group container alone.
          </li>
          <li>
            Teams may use organization <strong>Permanent</strong> (Sports, Admin) or school-year
            periods — Permanent is allowed for non-org teams.
          </li>
          <li>
            Access stays coupled to period validity on purpose. Past-period login via{' '}
            <code>override_period</code> shows a nav warning — stale context is a write hazard.
          </li>
          <li>
            Early enrollment forms: attribution follows the subject / target context, not only the
            session UGP (see temporal playbook).
          </li>
        </ul>
      </GuideSection>

      <GuideSection
        id="why-ugp-periods"
        title="Why UGP &amp; periods? (doubt resolver)"
        summary="Not an extra layer — shared time + stable membership identity"
      >
        <p>
          When this feels like ceremony, reopen the full note:{' '}
          <code>docs/why-ugp-and-periods.md</code>. Short version below.
        </p>
        <h3>The doubt</h3>
        <p>
          Why not <code>user → group</code> with start/end on that row? Why go through{' '}
          <code>groups_periods</code>?
        </p>
        <h3>The stack</h3>
        <pre className="mmg-tree">{`group          → timeless container (“Students”)
period         → named shared window (“2026–27”, “Permanent”)
groups_periods → that group in that window (the cohort)
UGP            → this user in that cohort (+ optional override dates)`}</pre>
        <p>
          UGP dates specialize a period (“still Fall Students, access held open”). They do not
          replace periods.
        </p>
        <h3>Why periods (not only per-user dates)</h3>
        <ul>
          <li>
            <strong>Shared control</strong> — end the year once for the cohort, not 800 private
            date rows that drift.
          </li>
          <li>
            <strong>Named cohorts</strong> — roster / permission / apps attach to a group–period,
            not a bare group.
          </li>
          <li>
            <strong>Nesting &amp; rollover</strong> — “Students · this year” is a real object; next
            year is a new GP + new UGPs; history stays.
          </li>
        </ul>
        <h3>Why identity needs UGP</h3>
        <p>
          6th-grade UGP does assignment A; 7th-grade UGP does assignment B. Submissions point at a{' '}
          <strong>membership</strong>. Without that you’d replay update history or trust a snapshot
          of a living row that may later be “corrected.” Old UGP rows stay true; new year = new
          row.
        </p>
        <p>
          <strong>North star:</strong> authenticate as user; act / attribute / authorize as current
          UGP. Periods = shared time + cohorts. UGP = stable membership identity.
        </p>
        <p className="mmg-note">
          Staff ops continuity (Coordinators / Admins) often uses <strong>Permanent</strong> anchors
          — filter “this year’s events” on data, don’t expire their membership to get a date
          filter. See the doc for Permanent vs term and user_id vs ugp_id heuristics.
        </p>
      </GuideSection>

      <GuideSection
        id="permissions-platform"
        title="Permissions &amp; multi-site (SIS as brain)"
        summary="page = UIS routes; platform = child products; full note in docs"
      >
        <p>
          Full convention: <code>docs/permissions-platform-and-pages.md</code>.
        </p>
        <h3>Sort key — page vs platform</h3>
          <pre className="mmg-tree">{`platform = main | acuitycte | learnpath | acuitylearn | ots | acuitydms
resource_type = page | capability | platform
resource_id = groups | my-hours | newsletter.ViewAllAccounts | *
permission_type = page_access | page_configuration | capability | platform_access
permission_name = optional human title (not the catalog key)`}</pre>
        <p>
          Column <code>platform</code> is the product namespace (<code>main</code> = UIS brain).{' '}
          <code>resource_id</code> is the page/capability key; <code>permission_name</code> is only a
          readable title. Child product enable uses <code>platform_access</code> with{' '}
          <code>resource_id=*</code>. Full note: <code>docs/permissions-platform-and-pages.md</code>.
        </p>
        <h3>Anchor ∪ extending (Phase 4 — not shipped)</h3>
        <p>
          Effective SIS page grants = active <strong>anchor</strong> ∪ same-org{' '}
          <strong>extending</strong> (optional). Anchors never union with each other. Most extending
          groups carry <strong>zero</strong> grants. Today is still winner-takes-all by grantee type
          (user &gt; UGP &gt; groups_periods).
        </p>
        <h3>Three stacks — keep separate</h3>
        <ul>
          <li>
            <strong>Page / capability permissions</strong> — staff surfaces &amp; verbs (UIS{' '}
            <code>page</code> or child <code>platform</code>)
          </li>
          <li>
            <strong>Assignment assignee policy</strong> — submission → assignment → form defaults
            (not page stacking)
          </li>
          <li>
            <strong>TOR roster</strong> — person links define <em>who</em>; page config defines{' '}
            <em>what caps</em>
          </li>
        </ul>
        <h3>Child sites under the SIS brain</h3>
        <p>
          Children <strong>consume</strong> claims (e.g. who has{' '}
          <code>platform/acuitycte/capability/training_plan.notify</code>). Prefer{' '}
          <strong>groups_periods</strong> grantees. Membership (Coordinator UGP) ≠ app metadata
          flags. Profile plugins later use <strong>apps</strong>, not{' '}
          <code>resource_type = app</code> for notify/review.
        </p>
        <p className="mmg-note">
          Shared cross-child verbs can use a dedicated platform id (or <code>main</code>) so a child
          filtering only to itself does not hide them.
        </p>
      </GuideSection>

      <GuideSection
        id="display-labels"
        title="Display labels (District, Program, School)"
        summary="ATA is a District — singular type vocabulary on groups, not tags"
      >
        <p>
          The org’s <strong>name</strong> stays ATA. ATA <strong>is a</strong> District — a
          client-facing kind of <code>organization</code> — while SQL still uses{' '}
          <code>group_type = 'organization'</code>. A nested team may <strong>be a</strong> Program
          without renaming that team’s title to “Program.”
        </p>
        <p>
          <strong>Singular <code>display_label</code></strong> (or{' '}
          <code>display_group_type</code> if that name reads clearer) on <code>groups</code> —{' '}
          <strong>not</strong> <code>display_labels[]</code>. An array would turn it into tags and
          confuse badges / “Create …” copy. One noun replaces how we say organization/team in the
          UI.
        </p>
        <ul>
          <li>
            Keep <code>group_type</code> = organization / team / system (hierarchy SQL)
          </li>
          <li>
            Keep <code>membership_kind</code> = anchor / extending
          </li>
          <li>
            Keep <code>name</code> = instance title (“ATA”)
          </li>
          <li>
            Optional org-level vocabulary defaults when label is null
          </li>
        </ul>
      </GuideSection>

      <GuideSection id="phases" title="Implementation phases" summary="What shipped vs what’s next">
        <ol>
          <li>
            <strong>Marker ✅</strong> — <code>membership_kind</code> column; create UI toggle;
            nested-groups API returns kind; anchor pill on tree/list.
          </li>
          <li>
            <strong>Switcher hygiene</strong> — switcher and <code>current_ugp_id</code> eligibility
            = anchors only.
          </li>
          <li>
            <strong>Profile aggregation</strong> — extending sections + click-through;{' '}
            <strong>Awards</strong> section (persistent).
          </li>
          <li>
            <strong>Permission union</strong> — anchor ∪ same-org extending grants.
          </li>
        </ol>
        <p>
          Full prose: <code>docs/anchor-and-extending-memberships.md</code> ·{' '}
          <code>docs/temporal-and-membership-scenarios.md</code> ·{' '}
          <code>docs/why-ugp-and-periods.md</code> ·{' '}
          <code>docs/permissions-platform-and-pages.md</code>.
        </p>
      </GuideSection>

      <GuideSection
        id="future-tasks"
        title="Future tasks &amp; open concerns"
        summary="Phased work, warnings, awards UI, and whether parent periods should cut access"
        defaultOpen
      >
        <h3>Planned / discussed follow-ups</h3>
        <ul>
          <li>
            <strong>Phase 2 — switcher &amp; defaults:</strong> filter Navigation +{' '}
            <code>ELIGIBLE_UGP_BASE</code> / membership APIs so only <strong>anchor</strong> UGPs
            are switchable or set as <code>current_ugp_id</code>.
          </li>
          <li>
            <strong>Phase 3 — profile:</strong> aggregate same-org extending UGPs (window overlap);
            membership detail route; <strong>Awards</strong> section that accumulates across years
            (load awards across same-org anchors, not only the viewing UGP).
          </li>
          <li>
            <strong>Phase 4 — permissions:</strong> effective grants = active anchor ∪ same-org
            extending (anchors still never stack with each other). Still{' '}
            <em>direct UGP only</em> — no walk up to Sports / org.
          </li>
          <li>
            <strong>Ancestor inheritance (opt-in, later):</strong> let an extending parent (Sports,
            or eventually the org) declare relationships / grants / profile sections that apply to
            members enrolled in descendants — e.g. Varsity · 2025 anchor gets a “sports athlete”
            relationship from Sports without a UGP on Sports itself. Must be{' '}
            <strong>explicit</strong> (flag or relationship type on the parent gp / group), not
            silent “everything on every ancestor cascades.” Keeps org/Sports as{' '}
            <code>extending</code> meaningful without inventing direct members on empty containers.
            Do <strong>not</strong> cascade period expiry via ancestors (see concern below).
          </li>
          <li>
            <strong>membership_kind for org / empty containers:</strong> keep{' '}
            <code>anchor</code> | <code>extending</code> only; set organization (+ Sports /
            Basketball shells) to <code>extending</code>. Revisit a third value only if inheritance
            needs a distinct “structural-only” marker.
          </li>
          <li>
            <strong>Admin override warning:</strong> when enrollment UI can set{' '}
            <code>override_period</code> on an <em>ended</em> period UGP, warn that it re-opens
            stale-context writes (user-side nav warning already ships).
          </li>
          <li>
            <strong>Read-only past context:</strong> browse expired UGPs / history without write
            capability — preferred over keeping old years switchable.
          </li>
          <li>
            <strong>Change membership_kind UI:</strong> none yet; if added, require pre-flight
            (member count, switcher impact, duplicate-anchor check). Prefer new group + move
            members for structural mistakes.
          </li>
          <li>
            <strong>Platform permissions:</strong> <code>product</code> / <code>site_source</code> +
            capability grants administered in SIS; child sites consume claims (TOR packs, etc.).
          </li>
          <li>
            <strong>Display labels:</strong> <code>groups.display_label</code> (+ optional org
            vocabulary defaults) for District / Program / School — not on gps.
          </li>
          <li>
            <strong>Data curation:</strong> backfill left all groups as <code>anchor</code>; set
            Sports / Basketball / clubs to <code>extending</code> via controlled SQL.
          </li>
          <li>
            <strong>Optional live Dean’s List roster</strong> for the current term, still writing
            persistent <strong>award</strong> rows for history.
          </li>
        </ul>

        <h3>Concern: ending a parent period (e.g. Sports) — cascading access?</h3>
        <p>
          Question: if Sports (above all teams) gets a limited / ended period, should everyone
          under Basketball / Team A lose access because an ancestor period ended?
        </p>
        <p>
          <strong>Today (approx.):</strong> login / acting access is driven by{' '}
          <em>the member’s own</em> UGP — linked school-year or Permanent dates, or{' '}
          <code>ugp.start_date</code> / <code>end_date</code> when <code>override_period</code> is
          on. Eligibility also requires an active hierarchy <em>relationship</em> window on the
          member’s <strong>immediate</strong> parent link. There is <strong>no</strong> full walk
          of ancestor periods (Sports → Basketball → roster) that auto-expires all children when
          Sports’ Permanent ends. One path (<code>current-membership</code>) also checks the{' '}
          <em>immediate</em> parent gp’s period dates (historically framed as “org period”), not
          the whole superparent chain.
        </p>
        <p>
          <strong>Recommendation — keep it this way (no ancestor-period cascade):</strong>
        </p>
        <ul>
          <li>
            Cascading parent/superparent period ends into every nested UGP gets messy fast
            (Permanent containers, mid-tree teams on different calendars, Guest / Exchange mixes,
            “who ended and why”). Admins would silently lock out rosters by shortening Sports.
          </li>
          <li>
            Access should stay explicit on <strong>each enrollment</strong> (UGP + its gp’s period /
            override). To retire a subtree, <strong>deliberately</strong> end hierarchy
            relationships, re-link teams, or end/unenroll specific UGPs — not inherit Sports’
            calendar.
          </li>
          <li>
            Ending Sports’ period or Permanent row is a rare structural change; treat it like a
            migration (relink children under a new Sports gp, or end <code>team_nested</code>{' '}
            links), with tooling later if needed — not runtime “parent expired → everyone locked
            out.”
          </li>
        </ul>
        <p className="mmg-note">
          Document this as intentional: parent periods organize nesting and admin context;
          they do not automatically inherit into child login access. If we ever need a
          “freeze Sports whole subtree” kill-switch, add an explicit admin action — not silent
          date inheritance.
        </p>
      </GuideSection>

      <footer className="mmg-footer">
        <button
          type="button"
          className="mmg-expand-all"
          onClick={() => {
            document.querySelectorAll('.mmg-section:not(.is-open) .mmg-section-toggle').forEach((b) => {
              b.click();
            });
          }}
        >
          Expand all sections
        </button>
      </footer>
    </div>
  );
}
